import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Tag, Tooltip } from '~/components';
import { useScrollCollapseContext } from '~/components/ScrollCollapse/hooks/useScrollCollapseContext';
import { cn } from '~/lib/cn';
import { useIsContactInfoEnabled } from '~/modules/artist-roster/hooks/useIsContactInfoEnabled';
import { getCountryName } from '~/modules/artist-roster/lib/artists';
import type { ArtistDetails } from '~/modules/artist-roster/lib/types';
import { formatCount } from '~/modules/artist-roster/lib/utils';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

import { ContactInfoPopover } from './ContactInfoPopover';

interface ArtistHeaderDetailsProps {
  artistData?: ArtistDetails;
}

export function ArtistHeaderDetails({ artistData }: ArtistHeaderDetailsProps) {
  const { t } = useTranslation('roster');
  const { t: nameTranslation } = useTranslation('names');
  const { trackEvent } = useAnalytics('Artist Roster');
  const { enabled: isArtistContactsEnabled } = useIsContactInfoEnabled();

  const {
    scrollProgress,
    easingFunction,
    transitionDuration,
    getInterpolatedValue,
  } = useScrollCollapseContext();
  const isHeaderCollapsed = scrollProgress > 1;

  if (!artistData) {
    console.log('NO ARTIST DATA');

    return null;
  }

  const handleSocialIconButtonClick = () => {
    trackEvent('artistHeroInteraction', {
      artist_id: artistData.partyId,
      artist_name: artistData.name,
    });
  };

  const handleDataToolsLinkClick = handleSocialIconButtonClick;

  return (
    <div
      className={cn(
        'mb-lg flex w-full justify-center will-change-[gap]',
        easingFunction,
        transitionDuration,
      )}
      style={{
        // Transition from "gap-2xl" to "gap-lg".
        gap: getInterpolatedValue(40, 24),
        // Transition from "mb-lg" to "12px".
        marginBottom: getInterpolatedValue(24, 12),
      }}
    >
      <div
        className={cn(
          'flex aspect-square items-center justify-center will-change-[height] bg-level-03',
          easingFunction,
          transitionDuration,
          {
            '!h-[56px]': isHeaderCollapsed,
          },
        )}
        // Transition from "h-[148px]" to "h-[80px]".
        style={{ height: getInterpolatedValue(148, 80) }}
      >
        {artistData.image ? (
          <img
            src={artistData.image}
            alt={artistData.name}
            className="size-full object-cover"
          />
        ) : (
          <Icon
            type="user"
            variant="light"
            aria-label={t('labels.emptyAvatar')}
          />
        )}
      </div>
      <div className="flex grow flex-col items-start justify-center self-end text-body-sm">
        <div
          className={cn(
            'flex items-center gap-sm will-change-[margin]',
            easingFunction,
            transitionDuration,
          )}
          // Transition from "mb-sm" to "mb-0".
          style={{ marginBottom: getInterpolatedValue(8, 0) }}
        >
          <h1
            className={cn(
              'font-bold will-change-[font-size]',
              easingFunction,
              transitionDuration,
            )}
            // Transition from "text-display-md" to "text-headline-sm".
            style={{ fontSize: getInterpolatedValue(32, 18) }}
          >
            {artistData.name}
          </h1>
          {/* @TODO WMG1-572 - Put back in once back end data is fixed. */}
          {/* {artistData.isCrossover ? <ArtistCrossoverIcon /> : null} */}
          {isArtistContactsEnabled ? (
            <span>
              <ContactInfoPopover partyId={artistData.partyId} />
            </span>
          ) : null}
        </div>
        <div
          className={cn(
            'flex gap-sm will-change-[margin-bottom]',
            easingFunction,
            transitionDuration,
          )}
          // Transition from "mb-sm" to "mb-0".
          style={{ marginBottom: getInterpolatedValue(8, 0) }}
        >
          <p>
            {artistData.countryOfOriginISO
              ? getCountryName(
                  artistData.countryOfOriginISO,
                  navigator.language,
                )
              : ''}
          </p>
          <div className="border-s-px border-emphasis-subtle" />
          <p>
            {t('details.rosters')}:{' '}
            <span>
              {artistData.labels.map((label) => label.name).join(', ')}
            </span>
          </p>
        </div>
        <div
          className={cn(
            'flex w-full items-center justify-between will-change-[height,opacity]',
            easingFunction,
            transitionDuration,
            {
              'h-[37px] opacity-full': !isHeaderCollapsed,
              'pointer-events-none h-0 opacity-none': isHeaderCollapsed,
            },
          )}
        >
          <div className="flex items-center justify-center gap-md">
            {artistData.socials.map((social) => {
              return (
                <div
                  key={`${social.name}_${social.url}`}
                  className="flex items-center gap-xs rounded-full"
                >
                  <Tooltip
                    content={
                      <span>
                        <Trans
                          t={t}
                          i18nKey={
                            social.name === 'youtube'
                              ? 'labels.subscriberFrom'
                              : 'labels.followerFrom'
                          }
                          values={{
                            number: social.followerCount
                              ? social.followerCount.toLocaleString()
                              : 0,
                            social: nameTranslation(social.name),
                          }}
                          components={{
                            strong: <span className="font-semibold" />,
                          }}
                        />
                      </span>
                    }
                    delayDuration={0}
                    side="bottom"
                    sideOffset={4}
                    align="start"
                    triggerAsChild
                    size="md"
                  >
                    <Link
                      to={social.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={social.name}
                      onClick={handleSocialIconButtonClick}
                      className="flex items-center gap-sm rounded-full px-md py-sm transition duration-300 hover:bg-emphasis-strong"
                    >
                      <Icon variant="brands" type={social.name} />
                      <span>{formatCount(social.followerCount)}</span>
                    </Link>
                  </Tooltip>
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-center gap-sm">
            {artistData.reports.map((report) => (
              <a
                key={report.name}
                href={report.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleDataToolsLinkClick}
              >
                <Tag variant="secondary">
                  {report.displayName ?? report.name}
                  <Icon
                    type="external-link"
                    size="sm"
                    variant="light"
                    role="presentation"
                    aria-hidden
                    aria-label="external-url-icon"
                    className="-mr-px text-white"
                  />
                </Tag>
              </a>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

ArtistHeaderDetails.displayName = 'ArtistHeaderDetails';
